import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  Clock,
  Calendar,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Layers,
  Plus,
  Disc,
  FileText,
  ListPlus,
  X,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';
import { Card, Button } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { SessionLog, WorkSession } from '../../components/Projects/SessionLog';
import { AttachmentManager } from '../../components/Projects/AttachmentManager';
import { SubProjectMiniCard, CreateSubProjectModal, BulkCreatePieces } from '../../components/Projects';
import { NotesPanel } from '../../components/WorkNotes';
import {
  projectsApi,
  ProjectDto,
  ProjectProgress as ProgressDto,
  flowSessionsApi,
  FlowSessionDto,
  attachmentsApi,
  ProjectAttachmentDto,
  CreateSubProjectRequest,
  CreateProjectRequest,
  workNotesApi,
} from '../../services';
import { useSubProjects } from '../../hooks';
import './ProjectDetails.css';

interface StageIndicatorProps {
  progress: ProgressDto;
}

// Stage Transition Modal - for progress/regression notes
interface StageTransitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
  fromStage: string;
  toStage: string;
  isAdvancing: boolean;
  isSubmitting: boolean;
}

const StageTransitionModal: React.FC<StageTransitionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  fromStage,
  toStage,
  isAdvancing,
  isSubmitting,
}) => {
  const [note, setNote] = useState('');

  // Reset note when modal opens
  useEffect(() => {
    if (isOpen) {
      setNote('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(note);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onConfirm(note);
    }
  };

  return (
    <div className="stage-transition-modal__overlay" onClick={onClose}>
      <div className="stage-transition-modal" onClick={e => e.stopPropagation()}>
        <div className="stage-transition-modal__header">
          <h2 className="stage-transition-modal__title">
            {isAdvancing ? (
              <>
                <ArrowRight size={20} className="stage-transition-modal__icon stage-transition-modal__icon--advance" />
                Advancing Stage
              </>
            ) : (
              <>
                <ArrowLeft size={20} className="stage-transition-modal__icon stage-transition-modal__icon--regress" />
                Going Back
              </>
            )}
          </h2>
          <button className="stage-transition-modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="stage-transition-modal__transition">
          <span className="stage-transition-modal__stage stage-transition-modal__stage--from">{fromStage}</span>
          {isAdvancing ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
          <span className="stage-transition-modal__stage stage-transition-modal__stage--to">{toStage}</span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="stage-transition-modal__field">
            <label className="stage-transition-modal__label">
              {isAdvancing ? 'Progress Note (optional)' : 'Reason for going back (optional)'}
            </label>
            <textarea
              className="stage-transition-modal__textarea"
              value={note}
              onChange={e => setNote(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isAdvancing
                ? "What was accomplished in this stage? Any notes for the next stage?"
                : "Why are you going back? What needs to be revisited?"
              }
              rows={4}
              autoFocus
            />
            <span className="stage-transition-modal__hint">⌘+Enter to confirm</span>
          </div>

          <div className="stage-transition-modal__actions">
            <Button variant="ghost" type="button" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant={isAdvancing ? 'primary' : 'secondary'}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : isAdvancing ? 'Advance' : 'Go Back'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Compact stage indicator for header
const StageIndicatorCompact: React.FC<StageIndicatorProps> = ({ progress }) => {
  return (
    <div className="stage-indicator-compact">
      <div className="stage-indicator-compact__stages">
        {progress.stages.map((stage) => (
          <div
            key={stage.name}
            className={`stage-indicator-compact__stage ${stage.isCompleted ? 'stage-indicator-compact__stage--completed' : ''} ${stage.isCurrent ? 'stage-indicator-compact__stage--current' : ''}`}
          >
            <div className="stage-indicator-compact__dot">
              {stage.isCompleted && <CheckCircle size={14} />}
            </div>
            <span className="stage-indicator-compact__name">{stage.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper to convert FlowSessionDto to WorkSession format for SessionLog component
const convertToWorkSession = (session: FlowSessionDto, currentStage: string): WorkSession => {
  let durationMinutes = 0;
  if (session.endTime) {
    const start = new Date(session.startTime).getTime();
    const end = new Date(session.endTime).getTime();
    durationMinutes = Math.round((end - start) / (1000 * 60));
  }

  return {
    id: session.id,
    projectId: session.projectId,
    startTime: session.startTime,
    endTime: session.endTime,
    duration: durationMinutes,
    notes: session.postJournalText,
    stage: currentStage,
  };
};

export const ProjectDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [project, setProject] = useState<ProjectDto | null>(null);
  const [progress, setProgress] = useState<ProgressDto | null>(null);
  const [sessions, setSessions] = useState<FlowSessionDto[]>([]);
  const [attachments, setAttachments] = useState<ProjectAttachmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateSubProjectModal, setShowCreateSubProjectModal] = useState(false);
  const [showBulkCreatePieces, setShowBulkCreatePieces] = useState(false);

  // Stage transition modal state
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState<'advance' | 'regress'>('advance');
  const [targetStage, setTargetStage] = useState<string>('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);

  // Determine if this is an Opus or Piece
  const isOpus = project?.category === 'Opus';
  const isPiece = project?.category === 'Piece';

  // Sub-projects management
  const {
    subProjects,
    isLoading: subProjectsLoading,
    createSubProject,
    loadSubProjects,
  } = useSubProjects({
    parentProjectId: id || '',
    autoLoad: false, // Load manually after project data is fetched
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const [projectData, progressData, sessionsData, attachmentsData] = await Promise.all([
          projectsApi.getProject(id),
          projectsApi.getProjectProgress(id),
          flowSessionsApi.getProjectSessions(id),
          attachmentsApi.getProjectAttachments(id),
        ]);
        setProject(projectData);
        setProgress(progressData);
        setSessions(sessionsData);
        setAttachments(attachmentsData);

        // Load sub-projects if this is a parent project or an Opus
        if (projectData.isParentProject || projectData.category === 'Opus') {
          loadSubProjects();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load work');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, loadSubProjects]);

  const handleComplete = async () => {
    if (!project) return;
    try {
      const updated = await projectsApi.completeProject(project.id);
      setProject(updated);
      const newProgress = await projectsApi.getProjectProgress(project.id);
      setProgress(newProgress);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete work');
    }
  };

  // Get previous stage name from progress
  const getPreviousStage = (): string | null => {
    if (!progress || progress.currentStageIndex === 0) return null;
    return progress.stages[progress.currentStageIndex - 1]?.name || null;
  };

  const previousStage = getPreviousStage();

  // Open advance modal
  const handleAdvanceClick = () => {
    if (!progress?.nextStage) return;
    setTargetStage(progress.nextStage);
    setTransitionDirection('advance');
    setShowTransitionModal(true);
  };

  // Open regress modal
  const handleRegressClick = () => {
    if (!previousStage) return;
    setTargetStage(previousStage);
    setTransitionDirection('regress');
    setShowTransitionModal(true);
  };

  // Confirm stage transition with optional note
  const handleStageTransition = async (note: string) => {
    if (!project || !targetStage) return;

    try {
      setIsTransitioning(true);

      // Move to the target stage
      const updated = await projectsApi.moveToStage(project.id, targetStage);
      setProject(updated);

      // Create a note if provided
      if (note.trim()) {
        const noteType = transitionDirection === 'advance' ? 'StatusChange' : 'Issue';
        const fromStage = progress?.currentStage || 'Unknown';
        const prefix = transitionDirection === 'advance'
          ? `✅ ${fromStage} → ${targetStage}`
          : `⬅️ ${fromStage} → ${targetStage}`;

        await workNotesApi.createNote(project.id, {
          content: `${prefix}\n\n${note.trim()}`,
          noteType,
          isPinned: false,
        });

        // Trigger notes panel refresh
        setNotesRefreshKey(prev => prev + 1);
      }

      // Refresh progress
      const newProgress = await projectsApi.getProjectProgress(project.id);
      setProgress(newProgress);

      setShowTransitionModal(false);
      setTargetStage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change stage');
    } finally {
      setIsTransitioning(false);
    }
  };

  // Session handlers
  const handleStartSession = useCallback(async () => {
    if (!project) return;
    try {
      setSessionsLoading(true);
      const newSession = await flowSessionsApi.startSession({
        projectId: project.id,
        preRitualCompleted: false,
      });
      setSessions(prev => [newSession, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start session');
    } finally {
      setSessionsLoading(false);
    }
  }, [project]);

  const handleEndSession = useCallback(async (sessionId: string) => {
    try {
      setSessionsLoading(true);
      const updatedSession = await flowSessionsApi.endSession(sessionId, {});
      setSessions(prev =>
        prev.map(s => (s.id === sessionId ? updatedSession : s))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end session');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  // Attachment handlers
  const handleAttachmentAdded = useCallback((attachment: ProjectAttachmentDto) => {
    setAttachments(prev => [...prev, attachment]);
  }, []);

  const handleAttachmentRemoved = useCallback((attachmentId: string) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  }, []);

  // Sub-project handlers
  const handleCreateSubProject = async (data: CreateSubProjectRequest) => {
    await createSubProject(data);
    // Reload project to update counts
    if (id) {
      const updatedProject = await projectsApi.getProject(id);
      setProject(updatedProject);
    }
  };

  const handleSubProjectClick = (subProject: ProjectDto) => {
    navigate(`/projects/${subProject.id}`);
  };

  // Bulk create pieces handler for Opus projects
  const handleBulkCreatePieces = async (pieces: Omit<CreateProjectRequest, 'workflowTemplateId'>[]) => {
    if (!id) return;

    // Create each piece sequentially using createSubProject API
    for (const pieceData of pieces) {
      await projectsApi.createSubProject(id, {
        title: pieceData.title,
        description: pieceData.description,
        projectType: pieceData.projectType,
        initialStage: pieceData.initialStage,
      });
    }

    // Reload sub-projects and project data
    loadSubProjects();
    const updatedProject = await projectsApi.getProject(id);
    setProject(updatedProject);
    setShowBulkCreatePieces(false);
  };

  if (loading) {
    return (
      <AppLayout showBackButton backPath="/projects" backLabel={t('works.backToWorks')}>
        <div className="project-details project-details--loading">
          <div className="loading-spinner" />
          <p>Loading work...</p>
        </div>
      </AppLayout>
    );
  }

  if (error || !project) {
    return (
      <AppLayout showBackButton backPath="/projects" backLabel={t('works.backToWorks')}>
        <div className="project-details project-details--error">
          <AlertCircle size={48} />
          <h2>Error Loading Work</h2>
          <p>{error || 'Work not found'}</p>
          <Link to="/projects">
            <Button variant="primary">Back to Works</Button>
          </Link>
        </div>
      </AppLayout>
    );
  }

  const isCompleted = project.status === 'Completed';
  const isArchived = project.status === 'Archived';

  return (
    <AppLayout showBackButton backPath="/projects" backLabel={t('works.backToWorks')}>
      <div className="project-details">
        {/* Header */}
        <header className="project-details__header">
          <div className="project-details__header-row">
            {/* Left: Title and meta */}
            <div className="project-details__title-section">
              <div className="project-details__title-row">
                <h1 className="project-details__title">{project.title}</h1>
              </div>
              <div className="project-details__meta">
                <span className="project-details__type">{project.projectType}</span>
                <div className="project-details__badges">
                  <div className={`project-details__status project-details__status--${project.status.toLowerCase()}`}>
                    {project.status}
                  </div>
                  {isOpus && (
                    <div className="project-details__category-badge project-details__category-badge--opus">
                      <Disc size={14} />
                      {t('works.categories.opus')}
                    </div>
                  )}
                  {isPiece && (
                    <div className="project-details__category-badge project-details__category-badge--piece">
                      <FileText size={14} />
                      {t('works.categories.piece')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Progress indicator - inline with title */}
            {progress && !isArchived && (
              <div className="project-details__header-progress">
                {/* Back button - only show if not at first stage */}
                {!isCompleted && previousStage && (
                  <Button variant="ghost" size="sm" onClick={handleRegressClick} className="stage-btn--back">
                    <ChevronLeft size={16} />
                    Back to {previousStage}
                  </Button>
                )}

                <StageIndicatorCompact progress={progress} />

                {/* Advance button */}
                {!isCompleted && progress.nextStage && (
                  <Button variant="primary" size="sm" onClick={handleAdvanceClick}>
                    Advance to {progress.nextStage}
                    <ChevronRight size={16} />
                  </Button>
                )}
                {!isCompleted && !progress.nextStage && (
                  <Button variant="primary" size="sm" onClick={handleComplete}>
                    <CheckCircle size={16} />
                    Mark Complete
                  </Button>
                )}
              </div>
            )}
          </div>
        </header>

      {/* Main Content */}
      <main className="project-details__content">
        <div className="project-details__main">
          {/* Description */}
          {project.description && (
            <Card className="project-details__section">
              <h3 className="project-details__section-title">Description</h3>
              <p className="project-details__description">{project.description}</p>
            </Card>
          )}

          {/* Pieces Section (for Opus projects) or Sub-Projects Section (for other parent projects) */}
          {(project.isParentProject || isOpus || !project.isSubProject) && (
            <Card className={`project-details__section project-details__sub-projects ${isOpus ? 'project-details__sub-projects--opus' : ''}`}>
              <div className="project-details__sub-projects-header">
                <div className="project-details__sub-projects-title">
                  {isOpus ? <Disc size={20} /> : <Layers size={20} />}
                  <h3>{isOpus ? t('works.pieces.title') : t('works.pieces.title')}</h3>
                  {project.subProjectCount > 0 && (
                    <span className="project-details__sub-projects-count">
                      {t('works.pieces.count', {
                        completed: project.completedSubProjectCount,
                        total: project.subProjectCount,
                      })}
                    </span>
                  )}
                </div>
                {!isArchived && (
                  <div className="project-details__sub-projects-actions">
                    {isOpus && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowBulkCreatePieces(true)}
                        title={t('works.pieces.bulkCreate')}
                      >
                        <ListPlus size={16} />
                        {t('works.pieces.bulkCreate')}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowCreateSubProjectModal(true)}
                    >
                      <Plus size={16} />
                      {t('works.pieces.addPiece')}
                    </Button>
                  </div>
                )}
              </div>

              {subProjectsLoading ? (
                <div className="project-details__sub-projects-loading">
                  <div className="loading-spinner loading-spinner--sm" />
                  <span>{isOpus ? t('works.pieces.loading') : t('works.pieces.loading')}</span>
                </div>
              ) : subProjects.length > 0 ? (
                <div className="project-details__sub-projects-list">
                  {subProjects.map((subProject) => (
                    <SubProjectMiniCard
                      key={subProject.id}
                      project={subProject}
                      onClick={handleSubProjectClick}
                      onWorkOnIt={handleSubProjectClick}
                      isPiece={isOpus}
                      expandable={isOpus}
                    />
                  ))}
                </div>
              ) : (
                <div className="project-details__sub-projects-empty">
                  <p>{isOpus ? t('works.pieces.noPieces') : t('works.pieces.noSubProjects')}</p>
                  <p className="project-details__sub-projects-hint">
                    {isOpus ? t('works.pieces.addFirst') : t('works.pieces.addFirst')}
                  </p>
                </div>
              )}
            </Card>
          )}

          {/* Work Notes - right after Pieces */}
          <NotesPanel
            projectId={project.id}
            currentStage={project.currentStage}
            refreshKey={notesRefreshKey}
          />

          {/* Parent Project Link (for sub-projects) */}
          {project.isSubProject && project.parentProjectId && (
            <Card className="project-details__section project-details__parent-link">
              <Link
                to={`/projects/${project.parentProjectId}`}
                className="project-details__parent-link-content"
              >
                <Layers size={18} />
                <span>{t('works.opus.viewOpus')}</span>
                <ChevronRight size={16} />
              </Link>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <aside className="project-details__sidebar">
          {/* Info Card */}
          <Card className="project-details__info">
            <h3 className="project-details__info-title">Details</h3>
            <div className="project-details__info-item">
              <Calendar size={16} />
              <div>
                <span className="project-details__info-label">Created</span>
                <span className="project-details__info-value">
                  {new Date(project.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
            {project.targetCompletionDate && (
              <div className="project-details__info-item">
                <Clock size={16} />
                <div>
                  <span className="project-details__info-label">Target</span>
                  <span className="project-details__info-value">
                    {new Date(project.targetCompletionDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}
            <div className="project-details__info-item">
              <ChevronRight size={16} />
              <div>
                <span className="project-details__info-label">Current Stage</span>
                <span className="project-details__info-value">{project.currentStage}</span>
              </div>
            </div>
          </Card>

          {/* Work Sessions */}
          <SessionLog
            projectId={project.id}
            sessions={sessions.map(s => convertToWorkSession(s, project.currentStage))}
            onStartSession={!isArchived && !isCompleted ? handleStartSession : undefined}
            onEndSession={!isArchived && !isCompleted ? handleEndSession : undefined}
            isLoading={sessionsLoading}
          />

          {/* Attachments */}
          <AttachmentManager
            projectId={project.id}
            attachments={attachments}
            onAttachmentAdded={handleAttachmentAdded}
            onAttachmentRemoved={handleAttachmentRemoved}
            readOnly={isArchived}
          />
        </aside>
      </main>

        {/* Create Sub-Project Modal */}
        <CreateSubProjectModal
          isOpen={showCreateSubProjectModal}
          onClose={() => setShowCreateSubProjectModal(false)}
          onSubmit={handleCreateSubProject}
          parentProjectTitle={project.title}
          defaultProjectType={project.projectType}
        />

        {/* Bulk Create Pieces Modal (for Opus projects) */}
        {showBulkCreatePieces && isOpus && (
          <BulkCreatePieces
            opusId={project.id}
            opusTitle={project.title}
            projectType={project.projectType}
            onSubmit={handleBulkCreatePieces}
            onCancel={() => setShowBulkCreatePieces(false)}
          />
        )}

        {/* Stage Transition Modal */}
        <StageTransitionModal
          isOpen={showTransitionModal}
          onClose={() => {
            setShowTransitionModal(false);
            setTargetStage('');
          }}
          onConfirm={handleStageTransition}
          fromStage={project.currentStage}
          toStage={targetStage}
          isAdvancing={transitionDirection === 'advance'}
          isSubmitting={isTransitioning}
        />

      </div>
    </AppLayout>
  );
};

export default ProjectDetails;
